import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  MagnifyingGlass,
  Trash,
  X,
  Warning,
  ArrowsClockwise,
  FileText,
  TreeStructure,
  GearSix,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  UploadSimple,
  Image as ImageIcon,
  ChatCircleDots,
  MegaphoneSimple,
  ChartBar,
  PaperPlaneRight,
  NotePencil,
  UserCircle,
  FunnelSimple,
  Star,
  WarningCircle,
  Broadcast,
  UsersThree,
  Play,
  Stop,
  BellRinging,
  Phone
} from '@phosphor-icons/react';
import { Header } from '../../components/layout';
import { Button, Modal } from '../../components/common';
import whatsappService from '../../services/whatsappService';
import { ChatContent } from '../Chat/Chat';
import './WhatsApp.css';

const STATUS_STYLES = {
  APPROVED: { icon: CheckCircle, className: 'status-approved' },
  PENDING: { icon: Clock, className: 'status-pending' },
  REJECTED: { icon: XCircle, className: 'status-rejected' },
  DRAFT: { icon: Clock, className: 'status-draft' },
  PUBLISHED: { icon: CheckCircle, className: 'status-approved' },
  DEPRECATED: { icon: XCircle, className: 'status-rejected' },
};

const COMPLAINT_STATUS_STYLES = {
  open: { label: 'Open', className: 'complaint-open' },
  in_progress: { label: 'In Progress', className: 'complaint-in-progress' },
  resolved: { label: 'Resolved', className: 'complaint-resolved' },
  closed: { label: 'Closed', className: 'complaint-closed' },
};

const PRIORITY_STYLES = {
  low: { label: 'Low', className: 'priority-low' },
  medium: { label: 'Medium', className: 'priority-medium' },
  high: { label: 'High', className: 'priority-high' },
  urgent: { label: 'Urgent', className: 'priority-urgent' },
};

// Quick-start starting points for the Create Template form — common WashX message
// types, one per Meta category. Users can still edit every field before submitting.
const TEMPLATE_PRESETS = [
  {
    key: 'order_confirmation',
    label: 'Order Confirmation',
    category: 'UTILITY',
    form: {
      name: 'order_confirmation', category: 'UTILITY', language: 'en',
      headerType: 'text', headerText: 'Order Confirmed', headerImageUrl: '',
      body: 'Hi {{1}}, your order {{2}} has been confirmed. Pickup scheduled for {{3}}. Thank you for choosing WashX!',
      footer: 'WashX Dry Cleaning & Laundry', buttons: []
    }
  },
  {
    key: 'pickup_reminder',
    label: 'Pickup Reminder',
    category: 'UTILITY',
    form: {
      name: 'pickup_reminder', category: 'UTILITY', language: 'en',
      headerType: 'none', headerText: '', headerImageUrl: '',
      body: 'Hi {{1}}, this is a reminder that your pickup for order {{2}} is scheduled today at {{3}}. Please keep your items ready.',
      footer: '', buttons: [{ type: 'QUICK_REPLY', text: 'Reschedule' }]
    }
  },
  {
    key: 'delivery_update',
    label: 'Delivery Update',
    category: 'UTILITY',
    form: {
      name: 'delivery_update', category: 'UTILITY', language: 'en',
      headerType: 'text', headerText: 'Your Order is Ready', headerImageUrl: '',
      body: 'Hi {{1}}, your order {{2}} is out for delivery and will arrive by {{3}}. Amount due: {{4}}.',
      footer: 'WashX Dry Cleaning & Laundry', buttons: []
    }
  },
  {
    key: 'payment_reminder',
    label: 'Payment Reminder',
    category: 'UTILITY',
    form: {
      name: 'payment_reminder', category: 'UTILITY', language: 'en',
      headerType: 'none', headerText: '', headerImageUrl: '',
      body: 'Hi {{1}}, a payment of {{2}} is pending for order {{3}}. Please pay at the time of delivery.',
      footer: '', buttons: []
    }
  },
  {
    key: 'order_feedback',
    label: 'Order Feedback',
    category: 'UTILITY',
    form: {
      name: 'order_feedback', category: 'UTILITY', language: 'en',
      headerType: 'none', headerText: '', headerImageUrl: '',
      body: 'Hi {{1}}, your WashX order {{2}} has been delivered ✅\n\nHow was your experience? Tap a rating below — it helps us serve you better.\n\nThank you for choosing WashX! 💙',
      footer: 'WashX Dry Cleaning & Laundry',
      buttons: [
        { type: 'QUICK_REPLY', text: 'Excellent' },
        { type: 'QUICK_REPLY', text: 'Good' },
        { type: 'QUICK_REPLY', text: 'Needs Improvement' }
      ]
    }
  },
  {
    key: 'otp_verification',
    label: 'OTP Verification',
    category: 'AUTHENTICATION',
    form: {
      name: 'otp_verification', category: 'AUTHENTICATION', language: 'en',
      headerType: 'none', headerText: '', headerImageUrl: '',
      body: '{{1}} is your WashX verification code. Do not share this code with anyone.',
      footer: '', buttons: []
    }
  },
  {
    key: 'promotional_offer',
    label: 'Promotional Offer',
    category: 'MARKETING',
    form: {
      name: 'promotional_offer', category: 'MARKETING', language: 'en',
      headerType: 'image', headerText: '', headerImageUrl: '',
      body: 'Hi {{1}}, enjoy {{2}} off your next order with WashX! Use code {{3}} at checkout. Offer valid till {{4}}.',
      footer: 'WashX Dry Cleaning & Laundry', buttons: [{ type: 'QUICK_REPLY', text: 'Book Now' }]
    }
  }
];

function getStatusDisplay(status) {
  const style = STATUS_STYLES[status] || { icon: Clock, className: 'status-pending' };
  const Icon = style.icon;
  return (
    <span className={`status-badge ${style.className}`}>
      <Icon size={14} />
      {status}
    </span>
  );
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true
  });
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Renders the full conversation thread with bot/admin/customer distinction + handover divider
function ConversationMessages({ messages, formatDateTime }) {
  if (!messages || messages.length === 0) {
    return <p className="no-messages-hint">No messages in this conversation.</p>;
  }

  // Find the index of the very first admin manual reply
  const firstAdminIdx = messages.findIndex(m => m.metadata?.sentByAdmin);

  return messages.map((msg, i) => {
    const isCustomer = msg.role === 'user';
    const isAdmin = !!msg.metadata?.sentByAdmin;
    // role === 'assistant' and not sent by admin → bot
    const isBot = !isCustomer && !isAdmin;

    const bubbleClass = isCustomer ? 'chat-customer' : isAdmin ? 'chat-admin' : 'chat-bot';

    return (
      <React.Fragment key={i}>
        {/* Show handover divider before the first admin message */}
        {firstAdminIdx >= 0 && i === firstAdminIdx && (
          <div className="handover-divider">
            <span className="handover-divider-line" />
            <span className="handover-divider-text">Bot conversation ended &middot; Admin took over</span>
            <span className="handover-divider-line" />
          </div>
        )}

        <div className={`chat-bubble ${bubbleClass}`}>
          <div className="chat-bubble-content">
            <div className="chat-sender-label">
              {isCustomer && <span className="label-customer">Customer</span>}
              {isBot && <span className="label-bot">Bot</span>}
              {isAdmin && <span className="label-admin">Admin</span>}
            </div>
            <p>{msg.content}</p>
            <span className="chat-time">{formatDateTime(msg.timestamp)}</span>
          </div>
        </div>
      </React.Fragment>
    );
  });
}

export function WhatsApp({ onMenuClick }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'conversations');
  const [templates, setTemplates] = useState([]);
  const [flows, setFlows] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [conversationsPagination, setConversationsPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [conversationsFilter, setConversationsFilter] = useState('all');
  const [, setSelectedConversation] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);

  // Complaints state
  const [complaints, setComplaints] = useState([]);
  const [complaintsPagination, setComplaintsPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [complaintsStatusFilter, setComplaintsStatusFilter] = useState('all');
  const [complaintsPriorityFilter, setComplaintsPriorityFilter] = useState('all');
  const [complaintStatusCounts, setComplaintStatusCounts] = useState({});
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [adminReply, setAdminReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);

  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsPagination, setCampaignsPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [showCampaignDetailModal, setShowCampaignDetailModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '', templateName: '', templateLanguage: 'en',
    targetType: 'all', filters: { societies: [], pincodes: [], hasOrders: false, noOrders: false },
    templateParams: [], flowToken: ''
  });
  const [campaignFormErrors, setCampaignFormErrors] = useState({});
  const [campaignSaving, setCampaignSaving] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewingAudience, setPreviewingAudience] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [showDeleteCampaignModal, setShowDeleteCampaignModal] = useState(false);
  const [deletingCampaignItem, setDeletingCampaignItem] = useState(null);

  // Template modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);

  // Create template form
  const [templateForm, setTemplateForm] = useState({
    name: '', category: 'MARKETING', language: 'en',
    headerType: 'none', headerText: '', headerImageUrl: '',
    body: '', footer: '', buttons: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Settings form
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState(null);
  const [alertPhoneInput, setAlertPhoneInput] = useState('');
  const [alertPhoneError, setAlertPhoneError] = useState('');

  // Image upload states
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [settingsUploading, setSettingsUploading] = useState(false);

  // ─── Data Fetching ───────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await whatsappService.getTemplates();
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFlows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await whatsappService.getFlows();
      setFlows(data.flows || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load flows');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [configData, uploadsData] = await Promise.all([
        whatsappService.getConfig(),
        whatsappService.getUploads()
      ]);
      setConfig(configData.config);
      setSettingsForm({
        useWelcomeTemplate: configData.config.useWelcomeTemplate || false,
        welcomeTemplateId: configData.config.welcomeTemplateId || '',
        welcomeTemplateName: configData.config.welcomeTemplateName || '',
        templateLanguage: configData.config.templateLanguage || 'en',
        templateImageUrl: configData.config.templateImageUrl || '',
        conversationGapHours: configData.config.conversationGapHours || 24,
        chatbotEndpoint: configData.config.chatbotEndpoint || '',
        isActive: configData.config.isActive,
        newOrderAlertTemplateName: configData.config.newOrderAlertTemplateName || 'washx_new_order_alert',
        newOrderAlertPhones: configData.config.newOrderAlertPhones || [],
        orderFeedbackTemplateName: configData.config.orderFeedbackTemplateName || 'order_feedback'
      });
      setAlertPhoneInput('');
      setAlertPhoneError('');
      setUploadedImages(uploadsData.images || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConversations = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, limit: 20 };
      if (conversationsFilter !== 'all') params.status = conversationsFilter;
      if (searchTerm) params.search = searchTerm;
      const data = await whatsappService.getAllConversations(params);
      setConversations(data.conversations || []);
      setConversationsPagination(data.pagination || { page: 1, total: 0, pages: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [conversationsFilter, searchTerm]);

  const fetchComplaints = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, limit: 20 };
      if (complaintsStatusFilter !== 'all') params.status = complaintsStatusFilter;
      if (complaintsPriorityFilter !== 'all') params.priority = complaintsPriorityFilter;
      if (searchTerm) params.search = searchTerm;
      const data = await whatsappService.getComplaints(params);
      setComplaints(data.complaints || []);
      setComplaintStatusCounts(data.statusCounts || {});
      setComplaintsPagination(data.pagination || { page: 1, total: 0, pages: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, [complaintsStatusFilter, complaintsPriorityFilter, searchTerm]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await whatsappService.getAnalytics();
      setAnalytics(data.analytics || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampaigns = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const data = await whatsappService.getCampaigns({ page, limit: 20 });
      setCampaigns(data.campaigns || []);
      setCampaignsPagination(data.pagination || { page: 1, total: 0, pages: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load summary stats for stat cards on initial mount (silently, no loading spinner)
  useEffect(() => {
    const loadSummaryStats = async () => {
      const [configRes, templatesRes, complaintsRes] = await Promise.allSettled([
        whatsappService.getConfig(),
        whatsappService.getTemplates(),
        whatsappService.getComplaints({ page: 1, limit: 1 })
      ]);
      if (configRes.status === 'fulfilled' && configRes.value?.config) {
        setConfig(configRes.value.config);
      }
      if (templatesRes.status === 'fulfilled') {
        setTemplates(templatesRes.value?.templates || []);
      }
      if (complaintsRes.status === 'fulfilled') {
        setComplaintStatusCounts(complaintsRes.value?.statusCounts || {});
      }
    };
    loadSummaryStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'templates') fetchTemplates();
    else if (activeTab === 'flows') fetchFlows();
    else if (activeTab === 'settings') fetchConfig();
    else if (activeTab === 'conversations') fetchConversations();
    else if (activeTab === 'complaints') fetchComplaints();
    else if (activeTab === 'analytics') fetchAnalytics();
    else if (activeTab === 'campaigns') fetchCampaigns();
  }, [activeTab, fetchTemplates, fetchFlows, fetchConfig, fetchConversations, fetchComplaints, fetchAnalytics, fetchCampaigns]);

  // ─── Filters ─────────────────────────────────────────────
  const filteredTemplates = templates.filter(t => {
    const search = searchTerm.toLowerCase();
    return t.name.toLowerCase().includes(search) ||
      t.category.toLowerCase().includes(search) ||
      t.status.toLowerCase().includes(search);
  });

  const filteredFlows = flows.filter(f => {
    const search = searchTerm.toLowerCase();
    return f.name.toLowerCase().includes(search) ||
      f.status.toLowerCase().includes(search);
  });

  // ─── Template CRUD ───────────────────────────────────────
  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '', category: 'MARKETING', language: 'en',
      headerType: 'none', headerText: '', headerImageUrl: '',
      body: '', footer: '', buttons: []
    });
    setFormErrors({});
    setShowCreateModal(true);
  };

  const parseTemplateToForm = (template) => {
    const form = {
      name: template.name || '',
      category: template.category || 'MARKETING',
      language: template.language || 'en',
      headerType: 'none',
      headerText: '',
      headerImageUrl: '',
      body: '',
      footer: '',
      buttons: []
    };

    for (const comp of template.components || []) {
      if (comp.type === 'HEADER') {
        if (comp.format === 'TEXT') {
          form.headerType = 'text';
          form.headerText = comp.text || '';
        } else if (comp.format === 'IMAGE') {
          form.headerType = 'image';
          form.headerImageUrl = comp.example?.header_handle?.[0] || '';
        }
      } else if (comp.type === 'BODY') {
        form.body = comp.text || '';
      } else if (comp.type === 'FOOTER') {
        form.footer = comp.text || '';
      } else if (comp.type === 'BUTTONS' && Array.isArray(comp.buttons)) {
        form.buttons = comp.buttons
          .filter((btn) => btn.type === 'QUICK_REPLY')
          .map((btn) => ({ type: 'QUICK_REPLY', text: btn.text || '' }));
      }
    }

    return form;
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm(parseTemplateToForm(template));
    setFormErrors({});
    setShowDetailModal(false);
    setShowCreateModal(true);
  };

  const handleApplyPreset = (preset) => {
    if (editingTemplate) return; // don't overwrite locked name/language mid-edit with a full preset
    setTemplateForm({ ...preset.form });
    setFormErrors({});
  };

  // Deep link from the Dashboard's "Create WhatsApp Template" quick action
  // (/whatsapp?tab=templates&create=1) opens straight into the create modal.
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      handleCreateTemplate();
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddButton = () => {
    if (templateForm.buttons.length >= 3) return;
    setTemplateForm({ ...templateForm, buttons: [...templateForm.buttons, { type: 'QUICK_REPLY', text: '' }] });
  };

  const handleRemoveButton = (index) => {
    setTemplateForm({ ...templateForm, buttons: templateForm.buttons.filter((_, i) => i !== index) });
  };

  const handleButtonChange = (index, field, value) => {
    const updated = [...templateForm.buttons];
    updated[index] = { ...updated[index], [field]: value };
    setTemplateForm({ ...templateForm, buttons: updated });
  };

  const buildTemplateComponents = () => {
    const components = [];
    if (templateForm.headerType === 'text' && templateForm.headerText.trim()) {
      components.push({ type: 'HEADER', format: 'TEXT', text: templateForm.headerText });
    } else if (templateForm.headerType === 'image') {
      components.push({
        type: 'HEADER', format: 'IMAGE',
        example: templateForm.headerImageUrl ? { header_handle: [templateForm.headerImageUrl] } : undefined
      });
    }
    components.push({ type: 'BODY', text: templateForm.body });
    if (templateForm.footer.trim()) components.push({ type: 'FOOTER', text: templateForm.footer });

    // Keep non-quick-reply buttons (FLOW / URL / PHONE) from the original template when editing
    const preservedButtons = (editingTemplate?.components || [])
      .find((c) => c.type === 'BUTTONS')
      ?.buttons
      ?.filter((btn) => btn.type && btn.type !== 'QUICK_REPLY') || [];

    const quickReplyButtons = templateForm.buttons
      .filter((btn) => btn.text?.trim())
      .map((btn) => ({ type: 'QUICK_REPLY', text: btn.text.trim() }));

    const allButtons = [...preservedButtons, ...quickReplyButtons];
    if (allButtons.length > 0) {
      components.push({ type: 'BUTTONS', buttons: allButtons });
    }

    return components;
  };

  const handleSaveTemplate = async () => {
    const errors = {};
    if (!editingTemplate) {
      if (!templateForm.name.trim()) {
        errors.name = 'Template name is required';
      } else if (!/^[a-z][a-z0-9_]*$/.test(templateForm.name)) {
        errors.name = 'Name must be lowercase, start with a letter, and use only letters, numbers, underscores';
      }
    }
    if (!templateForm.body.trim()) errors.body = 'Body text is required';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    const components = buildTemplateComponents();

    try {
      setSaving(true);
      if (editingTemplate) {
        await whatsappService.updateTemplate(editingTemplate.id, {
          category: templateForm.category,
          components
        });
      } else {
        await whatsappService.createTemplate({
          name: templateForm.name,
          language: templateForm.language,
          category: templateForm.category,
          components
        });
      }
      setShowCreateModal(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.error
          || (editingTemplate ? 'Failed to update template' : 'Failed to create template')
      });
    } finally {
      setSaving(false);
    }
  };

  const handleViewTemplate = (template) => { setSelectedTemplate(template); setShowDetailModal(true); };
  const handleDeleteClick = (template) => { setDeletingTemplate(template); setShowDeleteModal(true); };

  const handleDeleteTemplate = async () => {
    if (!deletingTemplate) return;
    try {
      setSaving(true);
      await whatsappService.deleteTemplate(deletingTemplate.id, deletingTemplate.name);
      setShowDeleteModal(false);
      setDeletingTemplate(null);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete template');
    } finally {
      setSaving(false);
    }
  };

  // ─── Settings ────────────────────────────────────────────
  const normalizeAlertPhoneInput = (phone) => {
    let digits = String(phone || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 10) digits = `91${digits}`;
    if (digits.length < 10 || digits.length > 15) return '';
    return digits;
  };

  const formatAlertPhoneDisplay = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) {
      return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
    }
    return digits ? `+${digits}` : phone;
  };

  const handleAddAlertPhone = () => {
    setAlertPhoneError('');
    const normalized = normalizeAlertPhoneInput(alertPhoneInput);
    if (!normalized) {
      setAlertPhoneError('Enter a valid phone (10-digit Indian or with country code)');
      return;
    }
    const existing = settingsForm.newOrderAlertPhones || [];
    if (existing.includes(normalized)) {
      setAlertPhoneError('This number is already on the list');
      return;
    }
    setSettingsForm({
      ...settingsForm,
      newOrderAlertPhones: [...existing, normalized]
    });
    setAlertPhoneInput('');
  };

  const handleRemoveAlertPhone = (phone) => {
    setSettingsForm({
      ...settingsForm,
      newOrderAlertPhones: (settingsForm.newOrderAlertPhones || []).filter((p) => p !== phone)
    });
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsSaving(true);
      setSettingsMessage(null);
      await whatsappService.updateConfig(settingsForm);
      setSettingsMessage({ type: 'success', text: 'Settings saved successfully' });
      fetchConfig();
    } catch (err) {
      setSettingsMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save settings' });
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleHeaderImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const data = await whatsappService.uploadImage(file);
      setTemplateForm(prev => ({ ...prev, headerImageUrl: data.image.url }));
    } catch (err) {
      setFormErrors(prev => ({ ...prev, headerImage: err.response?.data?.error || 'Failed to upload image' }));
    } finally {
      setUploading(false);
    }
  };

  const handleSettingsImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setSettingsUploading(true);
      const data = await whatsappService.uploadImage(file);
      setSettingsForm(prev => ({ ...prev, templateImageUrl: data.image.url }));
      setUploadedImages(prev => [data.image, ...prev]);
    } catch (err) {
      setSettingsMessage({ type: 'error', text: err.response?.data?.error || 'Failed to upload image' });
    } finally {
      setSettingsUploading(false);
    }
  };

  const handleDeleteUpload = async (filename) => {
    try {
      await whatsappService.deleteUpload(filename);
      setUploadedImages(prev => prev.filter(img => img.filename !== filename));
      if (settingsForm.templateImageUrl?.includes(filename)) {
        setSettingsForm(prev => ({ ...prev, templateImageUrl: '' }));
      }
    } catch (err) { console.error('Error deleting image:', err); }
  };

  const handleSelectUploadedImage = (url) => { setSettingsForm(prev => ({ ...prev, templateImageUrl: url })); };

  // ─── Conversation Actions ────────────────────────────────
  const handleViewConversation = async (conv) => {
    try {
      setSelectedConversation(conv);
      setConversationDetail(null);
      setShowChatModal(true);
      const data = await whatsappService.getConversationDetail(conv.id);
      setConversationDetail(data.conversation);
    } catch (err) {
      setError('Failed to load conversation detail');
    }
  };

  const handleEndConversation = async (phone) => {
    try {
      await whatsappService.endConversation(phone);
      fetchConversations();
      setShowChatModal(false);
    } catch (err) {
      setError('Failed to end conversation');
    }
  };

  // ─── Complaint Actions ───────────────────────────────────
  const handleViewComplaint = async (complaint) => {
    try {
      setSelectedComplaint(complaint);
      setConversationDetail(null);
      setShowComplaintModal(true);
      const data = await whatsappService.getConversationDetail(complaint.id);
      setConversationDetail(data.conversation);
      setAdminNote('');
      setAdminReply('');
    } catch (err) {
      setError('Failed to load complaint detail');
    }
  };

  const handleUpdateComplaintStatus = async (id, newStatus) => {
    try {
      await whatsappService.updateComplaintStatus(id, { complaintStatus: newStatus });
      fetchComplaints();
      if (conversationDetail && conversationDetail.id === id) {
        setConversationDetail(prev => ({ ...prev, complaintStatus: newStatus }));
      }
    } catch (err) {
      setError('Failed to update complaint status');
    }
  };

  const handleUpdateComplaintPriority = async (id, newPriority) => {
    try {
      await whatsappService.updateComplaintStatus(id, { complaintPriority: newPriority });
      fetchComplaints();
      if (conversationDetail && conversationDetail.id === id) {
        setConversationDetail(prev => ({ ...prev, complaintPriority: newPriority }));
      }
    } catch (err) {
      setError('Failed to update complaint priority');
    }
  };

  const handleAddNote = async () => {
    if (!adminNote.trim() || !selectedComplaint) return;
    try {
      setAddingNote(true);
      const data = await whatsappService.addAdminNote(selectedComplaint.id, adminNote);
      setConversationDetail(prev => ({ ...prev, adminNotes: data.adminNotes }));
      setAdminNote('');
    } catch (err) {
      setError('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleSendReply = async () => {
    if (!adminReply.trim() || !selectedComplaint) return;
    try {
      setSendingReply(true);
      await whatsappService.sendAdminReply(selectedComplaint.id, adminReply);
      setAdminReply('');
      // Refresh conversation detail to show new message
      const data = await whatsappService.getConversationDetail(selectedComplaint.id);
      setConversationDetail(data.conversation);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  // ─── Campaign Actions ──────────────────────────────────────
  const handleCreateCampaign = async () => {
    // Ensure templates are loaded for the dropdown
    if (templates.length === 0) {
      try {
        const data = await whatsappService.getTemplates();
        setTemplates(data.templates || []);
      } catch (err) { /* ignore - user can type manually */ }
    }
    setCampaignForm({
      name: '', templateName: '', templateLanguage: 'en',
      targetType: 'all', filters: { societies: [], pincodes: [], hasOrders: false, noOrders: false },
      templateParams: [{ name: 'customer', defaultValue: 'Customer', useCustomerName: true }],
      flowToken: '', scheduledAt: ''
    });
    setCampaignFormErrors({});
    setAudiencePreview(null);
    setShowCreateCampaignModal(true);
  };

  const handlePreviewAudience = async () => {
    try {
      setPreviewingAudience(true);
      const data = await whatsappService.previewAudience({
        targetType: campaignForm.targetType,
        filters: campaignForm.filters
      });
      setAudiencePreview(data);
    } catch (err) {
      setCampaignFormErrors({ submit: err.response?.data?.error || 'Failed to preview audience' });
    } finally {
      setPreviewingAudience(false);
    }
  };

  const handleSaveCampaign = async () => {
    const errors = {};
    if (!campaignForm.name.trim()) errors.name = 'Campaign name is required';
    if (!campaignForm.templateName.trim()) errors.templateName = 'Template name is required';
    if (Object.keys(errors).length > 0) { setCampaignFormErrors(errors); return; }

    try {
      setCampaignSaving(true);
      const payload = { ...campaignForm };
      // Convert datetime-local string to ISO date for backend
      if (payload.scheduledAt) {
        payload.scheduledAt = new Date(payload.scheduledAt).toISOString();
      } else {
        delete payload.scheduledAt;
      }
      await whatsappService.createCampaign(payload);
      setShowCreateCampaignModal(false);
      fetchCampaigns();
    } catch (err) {
      setCampaignFormErrors({ submit: err.response?.data?.error || 'Failed to create campaign' });
    } finally {
      setCampaignSaving(false);
    }
  };

  const handleSendCampaign = async (id) => {
    try {
      setSendingCampaign(true);
      await whatsappService.sendCampaign(id);
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send campaign');
    } finally {
      setSendingCampaign(false);
    }
  };

  const handleCancelCampaign = async (id) => {
    try {
      await whatsappService.cancelCampaign(id);
      fetchCampaigns();
      if (selectedCampaign?._id === id) {
        setSelectedCampaign(prev => ({ ...prev, status: 'cancelled' }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel campaign');
    }
  };

  const handleDeleteCampaignConfirm = async () => {
    if (!deletingCampaignItem) return;
    try {
      setCampaignSaving(true);
      await whatsappService.deleteCampaign(deletingCampaignItem._id);
      setShowDeleteCampaignModal(false);
      setDeletingCampaignItem(null);
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete campaign');
    } finally {
      setCampaignSaving(false);
    }
  };

  const handleViewCampaign = async (campaign) => {
    try {
      const data = await whatsappService.getCampaign(campaign._id);
      setSelectedCampaign(data.campaign);
      setShowCampaignDetailModal(true);
    } catch (err) {
      setError('Failed to load campaign details');
    }
  };

  const getCampaignStatusStyle = (status) => {
    switch (status) {
      case 'draft': return { className: 'status-draft', label: 'Draft' };
      case 'scheduled': return { className: 'status-pending', label: 'Scheduled' };
      case 'sending': return { className: 'status-pending', label: 'Sending' };
      case 'completed': return { className: 'status-approved', label: 'Completed' };
      case 'failed': return { className: 'status-rejected', label: 'Failed' };
      case 'cancelled': return { className: 'status-rejected', label: 'Cancelled' };
      default: return { className: 'status-draft', label: status };
    }
  };

  // ─── Refresh ─────────────────────────────────────────────
  const handleRefresh = () => {
    if (activeTab === 'templates') fetchTemplates();
    else if (activeTab === 'flows') fetchFlows();
    else if (activeTab === 'settings') fetchConfig();
    else if (activeTab === 'conversations') fetchConversations();
    else if (activeTab === 'complaints') fetchComplaints();
    else if (activeTab === 'analytics') fetchAnalytics();
    else if (activeTab === 'campaigns') fetchCampaigns();
    // 'chat' tab has its own internal refresh
  };

  // ─── Chat Tab: full-height embed (bypasses page-content padding) ─
  if (activeTab === 'chat') {
    return (
      <>
        <Header title="WhatsApp" onMenuClick={onMenuClick} />
        <div className="wa-chat-embed">
          <div className="wa-chat-tabs-strip">
            <button className="wa-chat-back-btn" onClick={() => setActiveTab('conversations')}>
              ← Back to WhatsApp
            </button>
          </div>
          <ChatContent />
        </div>
      </>
    );
  }

  // ─── Loading State ──────────────────────────────────────
  if (loading && !conversations.length && !complaints.length && !templates.length && !analytics) {
    return (
      <>
        <Header title="WhatsApp" onMenuClick={onMenuClick} />
        <div className="page-content">
          <div className="loading-state">
            <ArrowsClockwise className="loading-spinner" size={32} />
            <p>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  // ─── Render ──────────────────────────────────────────────
  return (
    <>
      <Header title="WhatsApp" onMenuClick={onMenuClick} />
      <div className="page-content">
        {/* Page Header */}
        <div className="whatsapp-header">
          <div className="whatsapp-header-content">
            <h2 className="section-title">WhatsApp Management</h2>
            <p className="section-subtitle">Conversations, complaints, templates & configuration</p>
          </div>
          <div className="whatsapp-header-actions">
            <button className="action-icon-btn" onClick={handleRefresh} title="Refresh">
              <ArrowsClockwise size={18} />
            </button>
            {activeTab === 'templates' && (
              <Button variant="primary" onClick={handleCreateTemplate}>
                <Plus size={18} /> Create Template
              </Button>
            )}
            {activeTab === 'campaigns' && (
              <Button variant="primary" onClick={handleCreateCampaign}>
                <Plus size={18} /> New Campaign
              </Button>
            )}
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button className="banner-close-btn" onClick={() => setError(null)}><X size={18} /></button>
          </div>
        )}

        {/* Stats */}
        <div className="whatsapp-stats">
          <div className="stat-card">
            <span className="stat-label">Conversations</span>
            <span className="stat-value">{conversationsPagination.total || conversations.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Complaints</span>
            <span className="stat-value stat-warning">{(complaintStatusCounts.open || 0) + (complaintStatusCounts.in_progress || 0)}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Templates</span>
            <span className="stat-value">{templates.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Status</span>
            <span className={`stat-value ${config?.isActive ? 'stat-success' : 'stat-danger'}`}>
              {config?.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs-header">
            <button className={`tab-btn ${activeTab === 'conversations' ? 'active' : ''}`}
              onClick={() => { setActiveTab('conversations'); setSearchTerm(''); }}>
              <ChatCircleDots size={18} /> Conversations
            </button>
            <button className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}>
              <ChatCircleDots size={18} /> Live Chat
            </button>
            <button className={`tab-btn ${activeTab === 'complaints' ? 'active' : ''}`}
              onClick={() => { setActiveTab('complaints'); setSearchTerm(''); }}>
              <MegaphoneSimple size={18} /> Complaints
              {(complaintStatusCounts.open || 0) > 0 && (
                <span className="tab-count complaint-count">{complaintStatusCounts.open}</span>
              )}
            </button>
            <button className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => { setActiveTab('analytics'); setSearchTerm(''); }}>
              <ChartBar size={18} /> Analytics
            </button>
            <button className={`tab-btn ${activeTab === 'campaigns' ? 'active' : ''}`}
              onClick={() => { setActiveTab('campaigns'); setSearchTerm(''); }}>
              <Broadcast size={18} /> Campaigns
              <span className="tab-count">{campaignsPagination.total || campaigns.length}</span>
            </button>
            <button className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
              onClick={() => { setActiveTab('templates'); setSearchTerm(''); }}>
              <FileText size={18} /> Templates
              <span className="tab-count">{templates.length}</span>
            </button>
            <button className={`tab-btn ${activeTab === 'flows' ? 'active' : ''}`}
              onClick={() => { setActiveTab('flows'); setSearchTerm(''); }}>
              <TreeStructure size={18} /> Flows
              <span className="tab-count">{flows.length}</span>
            </button>
            <button className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => { setActiveTab('settings'); setSearchTerm(''); }}>
              <GearSix size={18} /> Settings
            </button>
          </div>

          <div className="tab-content">

            {/* ═══════════════ CONVERSATIONS TAB ═══════════════ */}
            {activeTab === 'conversations' && (
              <>
                <div className="tab-toolbar">
                  <div className="search-wrapper">
                    <MagnifyingGlass className="search-icon" size={18} />
                    <input type="text" className="search-input" placeholder="Search by name or phone..."
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchConversations()} />
                  </div>
                  <div className="filter-group">
                    <FunnelSimple size={16} />
                    <select className="filter-select" value={conversationsFilter}
                      onChange={(e) => { setConversationsFilter(e.target.value); }}>
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="ended">Ended</option>
                    </select>
                  </div>
                </div>

                <div className="table-container">
                  {loading && conversations.length === 0 ? (
                    <div className="loading-state">
                      <ArrowsClockwise className="loading-spinner" size={28} />
                      <p>Loading conversations...</p>
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="empty-state">
                      <ChatCircleDots size={48} className="empty-state-icon" />
                      <p>No conversations found</p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Status</th>
                          <th>Intent</th>
                          <th>Messages</th>
                          <th>Last Active</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conversations.map((conv) => (
                          <tr key={conv.id}>
                            <td>
                              <div className="customer-cell">
                                <UserCircle size={20} className="customer-icon" />
                                <div>
                                  <span className="customer-name">{conv.customerName || 'Unknown'}</span>
                                  <span className="customer-phone">{conv.phone}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`conv-status ${conv.isActive ? 'conv-active' : 'conv-ended'}`}>
                                {conv.isActive ? 'Active' : 'Ended'}
                              </span>
                            </td>
                            <td>
                              <span className="intent-badge">{conv.intent || 'unknown'}</span>
                            </td>
                            <td>{conv.messageCount}</td>
                            <td>
                              <span className="time-ago">{timeAgo(conv.lastMessageAt)}</span>
                            </td>
                            <td>
                              <div className="actions-cell">
                                <button className="action-btn" onClick={() => handleViewConversation(conv)} title="View Chat">
                                  <Eye size={16} />
                                </button>
                                {conv.isActive && (
                                  <button className="action-btn danger" onClick={() => handleEndConversation(conv.phone)} title="End Conversation">
                                    <XCircle size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Pagination */}
                {conversationsPagination.pages > 1 && (
                  <div className="pagination">
                    <button disabled={conversationsPagination.page <= 1}
                      onClick={() => fetchConversations(conversationsPagination.page - 1)}>Previous</button>
                    <span>Page {conversationsPagination.page} of {conversationsPagination.pages}</span>
                    <button disabled={conversationsPagination.page >= conversationsPagination.pages}
                      onClick={() => fetchConversations(conversationsPagination.page + 1)}>Next</button>
                  </div>
                )}
              </>
            )}

            {/* ═══════════════ COMPLAINTS TAB ═══════════════ */}
            {activeTab === 'complaints' && (
              <>
                {/* Complaint Status Summary */}
                <div className="complaint-summary">
                  <div className={`complaint-summary-card ${complaintsStatusFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setComplaintsStatusFilter('all')}>
                    <span className="complaint-summary-count">{(complaintStatusCounts.open || 0) + (complaintStatusCounts.in_progress || 0) + (complaintStatusCounts.resolved || 0) + (complaintStatusCounts.closed || 0)}</span>
                    <span className="complaint-summary-label">All</span>
                  </div>
                  <div className={`complaint-summary-card open ${complaintsStatusFilter === 'open' ? 'active' : ''}`}
                    onClick={() => setComplaintsStatusFilter('open')}>
                    <span className="complaint-summary-count">{complaintStatusCounts.open || 0}</span>
                    <span className="complaint-summary-label">Open</span>
                  </div>
                  <div className={`complaint-summary-card in-progress ${complaintsStatusFilter === 'in_progress' ? 'active' : ''}`}
                    onClick={() => setComplaintsStatusFilter('in_progress')}>
                    <span className="complaint-summary-count">{complaintStatusCounts.in_progress || 0}</span>
                    <span className="complaint-summary-label">In Progress</span>
                  </div>
                  <div className={`complaint-summary-card resolved ${complaintsStatusFilter === 'resolved' ? 'active' : ''}`}
                    onClick={() => setComplaintsStatusFilter('resolved')}>
                    <span className="complaint-summary-count">{complaintStatusCounts.resolved || 0}</span>
                    <span className="complaint-summary-label">Resolved</span>
                  </div>
                </div>

                <div className="tab-toolbar">
                  <div className="search-wrapper">
                    <MagnifyingGlass className="search-icon" size={18} />
                    <input type="text" className="search-input" placeholder="Search complaints..."
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchComplaints()} />
                  </div>
                  <div className="filter-group">
                    <FunnelSimple size={16} />
                    <select className="filter-select" value={complaintsPriorityFilter}
                      onChange={(e) => setComplaintsPriorityFilter(e.target.value)}>
                      <option value="all">All Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>

                <div className="table-container">
                  {loading && complaints.length === 0 ? (
                    <div className="loading-state">
                      <ArrowsClockwise className="loading-spinner" size={28} />
                      <p>Loading complaints...</p>
                    </div>
                  ) : complaints.length === 0 ? (
                    <div className="empty-state">
                      <MegaphoneSimple size={48} className="empty-state-icon" />
                      <p>{searchTerm || complaintsStatusFilter !== 'all' ? 'No complaints found for this filter' : 'No complaints yet'}</p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Priority</th>
                          <th>Order ID</th>
                          <th>Received</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complaints.map((complaint) => (
                          <tr key={complaint.id} className={complaint.complaintPriority === 'urgent' ? 'row-urgent' : ''}>
                            <td>
                              <div className="customer-cell">
                                <UserCircle size={20} className="customer-icon" />
                                <div>
                                  <span className="customer-name">{complaint.customerName || 'Unknown'}</span>
                                  <span className="customer-phone">{complaint.phone}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="complaint-type-badge">{complaint.complaintType}</span>
                            </td>
                            <td>
                              <span className={`complaint-status-badge ${COMPLAINT_STATUS_STYLES[complaint.complaintStatus]?.className || ''}`}>
                                {COMPLAINT_STATUS_STYLES[complaint.complaintStatus]?.label || complaint.complaintStatus}
                              </span>
                            </td>
                            <td>
                              <span className={`priority-badge ${PRIORITY_STYLES[complaint.complaintPriority]?.className || ''}`}>
                                {PRIORITY_STYLES[complaint.complaintPriority]?.label || complaint.complaintPriority}
                              </span>
                            </td>
                            <td>
                              {complaint.complaintOrderId ? (
                                <span className="mono">{complaint.complaintOrderId}</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{formatDateTime(complaint.sessionStartedAt)}</td>
                            <td>
                              <div className="actions-cell">
                                <button className="action-btn" onClick={() => handleViewComplaint(complaint)} title="View & Manage">
                                  <Eye size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {complaintsPagination.pages > 1 && (
                  <div className="pagination">
                    <button disabled={complaintsPagination.page <= 1}
                      onClick={() => fetchComplaints(complaintsPagination.page - 1)}>Previous</button>
                    <span>Page {complaintsPagination.page} of {complaintsPagination.pages}</span>
                    <button disabled={complaintsPagination.page >= complaintsPagination.pages}
                      onClick={() => fetchComplaints(complaintsPagination.page + 1)}>Next</button>
                  </div>
                )}
              </>
            )}

            {/* ═══════════════ ANALYTICS TAB ═══════════════ */}
            {activeTab === 'analytics' && analytics && (
              <div className="analytics-container">
                <div className="analytics-grid">
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <ChatCircleDots size={24} />
                      <span>Total Conversations</span>
                    </div>
                    <div className="analytics-card-value">{analytics.totalConversations}</div>
                  </div>
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <CheckCircle size={24} />
                      <span>Active</span>
                    </div>
                    <div className="analytics-card-value analytics-success">{analytics.activeConversations}</div>
                  </div>
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <XCircle size={24} />
                      <span>Completed</span>
                    </div>
                    <div className="analytics-card-value">{analytics.completedConversations}</div>
                  </div>
                  <div className="analytics-card">
                    <div className="analytics-card-header">
                      <ChatCircleDots size={24} />
                      <span>Avg Messages</span>
                    </div>
                    <div className="analytics-card-value">{Math.round(analytics.avgMessagesPerConversation || 0)}</div>
                  </div>
                </div>

                {/* Intent Distribution */}
                <div className="analytics-section">
                  <h3 className="analytics-section-title">Intent Distribution</h3>
                  <div className="intent-distribution">
                    {(analytics.intentDistribution || []).map((item) => {
                      const total = analytics.totalConversations || 1;
                      const percentage = Math.round((item.count / total) * 100);
                      return (
                        <div key={item._id} className="intent-bar-row">
                          <span className="intent-bar-label">{item._id || 'unknown'}</span>
                          <div className="intent-bar-track">
                            <div className="intent-bar-fill" style={{ width: `${percentage}%` }} />
                          </div>
                          <span className="intent-bar-count">{item.count} ({percentage}%)</span>
                        </div>
                      );
                    })}
                    {(!analytics.intentDistribution || analytics.intentDistribution.length === 0) && (
                      <p className="text-muted">No data available</p>
                    )}
                  </div>
                </div>

                {/* Feedback Stats */}
                <div className="analytics-section">
                  <h3 className="analytics-section-title">Customer Feedback</h3>
                  <div className="feedback-stats">
                    <div className="feedback-stat-card">
                      <Star size={24} className="feedback-star" />
                      <span className="feedback-value">
                        {analytics.feedback?.avgRating ? analytics.feedback.avgRating.toFixed(1) : '-'}
                      </span>
                      <span className="feedback-label">Average Rating</span>
                    </div>
                    <div className="feedback-stat-card">
                      <span className="feedback-value">{analytics.feedback?.totalFeedback || 0}</span>
                      <span className="feedback-label">Total Feedback</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'analytics' && !analytics && !loading && (
              <div className="empty-state">
                <ChartBar size={48} className="empty-state-icon" />
                <p>No analytics data available</p>
              </div>
            )}

            {/* ═══════════════ TEMPLATES TAB ═══════════════ */}
            {activeTab === 'templates' && (
              <>
                <div className="tab-toolbar">
                  <div className="search-wrapper">
                    <MagnifyingGlass className="search-icon" size={18} />
                    <input type="text" className="search-input" placeholder="Search templates..."
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                </div>

                <div className="table-container">
                  {loading && templates.length === 0 ? (
                    <div className="loading-state">
                      <ArrowsClockwise className="loading-spinner" size={28} />
                      <p>Loading templates...</p>
                    </div>
                  ) : filteredTemplates.length === 0 ? (
                    <div className="empty-state">
                      <FileText size={48} className="empty-state-icon" />
                      <p>{searchTerm ? 'No templates found' : 'No templates yet'}</p>
                      {!searchTerm && (
                        <Button variant="primary" onClick={handleCreateTemplate}>
                          <Plus size={18} /> Create First Template
                        </Button>
                      )}
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Category</th>
                          <th>Language</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTemplates.map((template) => (
                          <tr key={template.id}>
                            <td>
                              <div className="template-name-cell">
                                <FileText size={18} className="template-icon" />
                                <span>{template.name}</span>
                              </div>
                            </td>
                            <td>{getStatusDisplay(template.status)}</td>
                            <td><span className="category-badge">{template.category}</span></td>
                            <td>{template.language}</td>
                            <td>
                              <div className="actions-cell">
                                <button className="action-btn" onClick={() => handleViewTemplate(template)} title="View Details">
                                  <Eye size={16} />
                                </button>
                                <button className="action-btn" onClick={() => handleEditTemplate(template)} title="Edit Template">
                                  <NotePencil size={16} />
                                </button>
                                <button className="action-btn danger" onClick={() => handleDeleteClick(template)} title="Delete">
                                  <Trash size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* ═══════════════ FLOWS TAB ═══════════════ */}
            {activeTab === 'flows' && (
              <>
                <div className="tab-toolbar">
                  <div className="search-wrapper">
                    <MagnifyingGlass className="search-icon" size={18} />
                    <input type="text" className="search-input" placeholder="Search flows..."
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                </div>

                <div className="table-container">
                  {loading && flows.length === 0 ? (
                    <div className="loading-state">
                      <ArrowsClockwise className="loading-spinner" size={28} />
                      <p>Loading flows...</p>
                    </div>
                  ) : filteredFlows.length === 0 ? (
                    <div className="empty-state">
                      <TreeStructure size={48} className="empty-state-icon" />
                      <p>{searchTerm ? 'No flows found' : 'No flows yet'}</p>
                      <p className="empty-state-hint">Flows are managed in Meta Business Manager</p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Categories</th>
                          <th>ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFlows.map((flow) => (
                          <tr key={flow.id}>
                            <td>
                              <div className="template-name-cell">
                                <TreeStructure size={18} className="template-icon" />
                                <span>{flow.name}</span>
                              </div>
                            </td>
                            <td>{getStatusDisplay(flow.status)}</td>
                            <td>
                              {(flow.categories || []).map((cat, i) => (
                                <span key={i} className="category-badge">{cat}</span>
                              ))}
                              {(!flow.categories || flow.categories.length === 0) && <span className="text-muted">-</span>}
                            </td>
                            <td><span className="text-muted mono">{flow.id}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* ═══════════════ CAMPAIGNS TAB ═══════════════ */}
            {activeTab === 'campaigns' && (
              <>
                <div className="table-container">
                  {loading && campaigns.length === 0 ? (
                    <div className="loading-state">
                      <ArrowsClockwise className="loading-spinner" size={28} />
                      <p>Loading campaigns...</p>
                    </div>
                  ) : campaigns.length === 0 ? (
                    <div className="empty-state">
                      <Broadcast size={48} className="empty-state-icon" />
                      <p>No campaigns yet</p>
                      <p className="empty-state-hint">Create your first WhatsApp campaign to reach your customers</p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Campaign</th>
                          <th>Template</th>
                          <th>Status</th>
                          <th>Recipients</th>
                          <th>Sent</th>
                          <th>Failed</th>
                          <th>Scheduled</th>
                          <th>Started</th>
                          <th>Completed</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaigns.map(c => {
                          const statusStyle = getCampaignStatusStyle(c.status);
                          return (
                            <tr key={c._id}>
                              <td>
                                <strong style={{ cursor: 'pointer', color: 'var(--primary)' }}
                                  onClick={() => handleViewCampaign(c)}>
                                  {c.name}
                                </strong>
                              </td>
                              <td><span className="mono">{c.templateName}</span></td>
                              <td><span className={`status-badge ${statusStyle.className}`}>{statusStyle.label}</span></td>
                              <td>{c.totalRecipients || '-'}</td>
                              <td><span className="stat-value stat-success">{c.sent || 0}</span></td>
                              <td><span className={c.failed > 0 ? 'stat-value stat-danger' : ''}>{c.failed || 0}</span></td>
                              <td className="time-ago">{c.scheduledAt ? formatDateTime(c.scheduledAt) : '-'}</td>
                              <td className="time-ago">{c.startedAt ? formatDateTime(c.startedAt) : '-'}</td>
                              <td className="time-ago">{c.completedAt ? formatDateTime(c.completedAt) : '-'}</td>
                              <td>
                                <div className="actions-cell">
                                  <button className="action-btn" onClick={() => handleViewCampaign(c)} title="View Details">
                                    <Eye size={16} />
                                  </button>
                                  {(c.status === 'draft' || c.status === 'scheduled') && (
                                    <button className="action-btn" onClick={() => handleSendCampaign(c._id)}
                                      disabled={sendingCampaign} title="Send Now">
                                      <Play size={16} />
                                    </button>
                                  )}
                                  {(c.status === 'sending' || c.status === 'scheduled') && (
                                    <button className="action-btn danger" onClick={() => handleCancelCampaign(c._id)}
                                      title="Cancel Campaign">
                                      <Stop size={16} />
                                    </button>
                                  )}
                                  {['draft', 'scheduled', 'completed', 'failed', 'cancelled'].includes(c.status) && (
                                    <button className="action-btn danger"
                                      onClick={() => { setDeletingCampaignItem(c); setShowDeleteCampaignModal(true); }}
                                      title="Delete Campaign">
                                      <Trash size={16} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Campaign Pagination */}
                {campaignsPagination.pages > 1 && (
                  <div className="pagination">
                    <button onClick={() => fetchCampaigns(campaignsPagination.page - 1)}
                      disabled={campaignsPagination.page <= 1}>Previous</button>
                    <span>Page {campaignsPagination.page} of {campaignsPagination.pages}</span>
                    <button onClick={() => fetchCampaigns(campaignsPagination.page + 1)}
                      disabled={campaignsPagination.page >= campaignsPagination.pages}>Next</button>
                  </div>
                )}
              </>
            )}

            {/* ═══════════════ SETTINGS TAB ═══════════════ */}
            {activeTab === 'settings' && (
              <div className="settings-container">
                {settingsMessage && (
                  <div className={`settings-message ${settingsMessage.type}`}>
                    {settingsMessage.type === 'success' ? <CheckCircle size={18} /> : <Warning size={18} />}
                    {settingsMessage.text}
                    <button onClick={() => setSettingsMessage(null)}><X size={14} /></button>
                  </div>
                )}

                <div className="settings-section">
                  <h3 className="settings-section-title">Welcome Template</h3>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <input type="checkbox" checked={settingsForm.useWelcomeTemplate || false}
                          onChange={(e) => setSettingsForm({ ...settingsForm, useWelcomeTemplate: e.target.checked })} />
                        Enable Welcome Template
                      </label>
                      <span className="form-hint">Send a template message when a new conversation starts</span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Template ID</label>
                      <input type="text" className="form-input"
                        value={settingsForm.welcomeTemplateId || ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, welcomeTemplateId: e.target.value })}
                        placeholder="e.g., 1377440960825346" />
                      <span className="form-hint">Meta template ID — template name will be resolved automatically</span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Template Name (fallback)</label>
                      <input type="text" className="form-input"
                        value={settingsForm.welcomeTemplateName || ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, welcomeTemplateName: e.target.value })}
                        placeholder="e.g., welcome_washx" />
                      <span className="form-hint">Used only if Template ID is not set or cannot be resolved</span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Template Language</label>
                      <select className="form-select" value={settingsForm.templateLanguage || 'en'}
                        onChange={(e) => setSettingsForm({ ...settingsForm, templateLanguage: e.target.value })}>
                        <option value="en">English (en)</option>
                        <option value="en_US">English US (en_US)</option>
                        <option value="hi">Hindi (hi)</option>
                        <option value="mr">Marathi (mr)</option>
                        <option value="ta">Tamil (ta)</option>
                        <option value="te">Telugu (te)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Template Header Image</label>
                      <div className="image-upload-area">
                        <label className={`upload-btn ${settingsUploading ? 'disabled' : ''}`}>
                          <UploadSimple size={16} />
                          {settingsUploading ? 'Uploading...' : 'Upload Image'}
                          <input type="file" accept="image/jpeg,image/png,image/webp"
                            onChange={handleSettingsImageUpload} disabled={settingsUploading} hidden />
                        </label>
                      </div>
                      {settingsForm.templateImageUrl && (
                        <div className="image-preview settings-image-preview">
                          <img src={settingsForm.templateImageUrl} alt="Template header" />
                          <button className="image-preview-remove"
                            onClick={() => setSettingsForm({ ...settingsForm, templateImageUrl: '' })} type="button">
                            <X size={12} />
                          </button>
                        </div>
                      )}
                      <span className="form-hint">Image shown in the template header. JPEG, PNG, or WebP. Max 5MB.</span>
                    </div>

                    {uploadedImages.length > 0 && (
                      <div className="form-group">
                        <label className="form-label">Uploaded Images</label>
                        <div className="uploaded-images-grid">
                          {uploadedImages.map((img) => (
                            <div key={img.filename}
                              className={`uploaded-image-card ${settingsForm.templateImageUrl === img.url ? 'selected' : ''}`}>
                              <img src={img.url} alt={img.filename}
                                onClick={() => handleSelectUploadedImage(img.url)} />
                              <div className="uploaded-image-actions">
                                <button className="uploaded-image-use"
                                  onClick={() => handleSelectUploadedImage(img.url)} title="Use this image" type="button">
                                  <ImageIcon size={12} />
                                </button>
                                <button className="uploaded-image-delete"
                                  onClick={() => handleDeleteUpload(img.filename)} title="Delete image" type="button">
                                  <Trash size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <span className="form-hint">Click an image to use it as the template header</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="settings-section">
                  <h3 className="settings-section-title">
                    <BellRinging size={18} weight="duotone" />
                    WhatsApp Order Alerts
                  </h3>
                  <p className="settings-section-desc">
                    These numbers receive a WhatsApp alert when a customer places an order through WhatsApp.
                  </p>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label className="form-label">Alert Template Name</label>
                      <input type="text" className="form-input"
                        value={settingsForm.newOrderAlertTemplateName || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          newOrderAlertTemplateName: e.target.value.trim().toLowerCase()
                        })}
                        placeholder="washx_new_order_alert" />
                      <span className="form-hint">Meta-approved template used for staff alerts</span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Feedback Template Name</label>
                      <input type="text" className="form-input"
                        value={settingsForm.orderFeedbackTemplateName || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          orderFeedbackTemplateName: e.target.value.trim().toLowerCase()
                        })}
                        placeholder="order_feedback" />
                      <span className="form-hint">
                        Sent with the invoice when an order is marked Delivered (Excellent / Good / Needs Improvement)
                      </span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Alert Recipients</label>
                      <div className="alert-phone-list">
                        {(settingsForm.newOrderAlertPhones || []).length === 0 ? (
                          <div className="alert-phone-empty">
                            <Phone size={18} />
                            <span>No recipients yet — add a phone number below</span>
                          </div>
                        ) : (
                          (settingsForm.newOrderAlertPhones || []).map((phone) => (
                            <div key={phone} className="alert-phone-row">
                              <div className="alert-phone-info">
                                <Phone size={16} />
                                <span className="alert-phone-number">{formatAlertPhoneDisplay(phone)}</span>
                                <span className="alert-phone-raw mono">{phone}</span>
                              </div>
                              <button type="button" className="action-btn danger"
                                onClick={() => handleRemoveAlertPhone(phone)}
                                title="Remove recipient">
                                <Trash size={16} />
                              </button>
                            </div>
                          ))
                        )}
                      </div>

                      <div className="alert-phone-add">
                        <input
                          type="tel"
                          className={`form-input ${alertPhoneError ? 'error' : ''}`}
                          value={alertPhoneInput}
                          onChange={(e) => {
                            setAlertPhoneInput(e.target.value);
                            if (alertPhoneError) setAlertPhoneError('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddAlertPhone();
                            }
                          }}
                          placeholder="e.g. 9876543210 or +91 98765 43210"
                        />
                        <Button variant="secondary" type="button" onClick={handleAddAlertPhone}>
                          <Plus size={16} /> Add
                        </Button>
                      </div>
                      {alertPhoneError && <span className="form-error">{alertPhoneError}</span>}
                      <span className="form-hint">
                        10-digit Indian numbers are stored with country code 91. Click Save Settings to apply.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="settings-section">
                  <h3 className="settings-section-title">Conversation Settings</h3>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label className="form-label">Conversation Gap Hours</label>
                      <input type="number" className="form-input"
                        value={settingsForm.conversationGapHours || 24}
                        onChange={(e) => setSettingsForm({ ...settingsForm, conversationGapHours: parseInt(e.target.value) || 24 })}
                        min={1} max={168} />
                      <span className="form-hint">Hours of inactivity before starting a new conversation session</span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Chatbot Endpoint</label>
                      <input type="text" className="form-input"
                        value={settingsForm.chatbotEndpoint || ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, chatbotEndpoint: e.target.value })}
                        placeholder="https://your-chatbot.example.com/webhook" />
                      <span className="form-hint">External chatbot webhook endpoint</span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <input type="checkbox" checked={settingsForm.isActive || false}
                          onChange={(e) => setSettingsForm({ ...settingsForm, isActive: e.target.checked })} />
                        WhatsApp Integration Active
                      </label>
                      <span className="form-hint">Enable or disable the entire WhatsApp integration</span>
                    </div>
                  </div>
                </div>

                <div className="settings-actions">
                  <Button variant="primary" onClick={handleSaveSettings} disabled={settingsSaving}>
                    {settingsSaving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════ CREATE / EDIT TEMPLATE MODAL ═══════════════ */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => { setShowCreateModal(false); setEditingTemplate(null); }}
          title={editingTemplate ? 'Edit WhatsApp Template' : 'Create WhatsApp Template'}
        >
          <div className="modal-form template-create-form">
            {formErrors.submit && (
              <div className="form-error-banner"><Warning size={18} />{formErrors.submit}</div>
            )}

            {editingTemplate && (
              <div className="form-hint" style={{ marginBottom: 12 }}>
                Editing <strong>{editingTemplate.name}</strong>. Name and language can’t be changed on Meta.
                Saving may send the template back to <strong>PENDING</strong> for re-approval.
              </div>
            )}

            {!editingTemplate && (
              <div className="form-group">
                <label className="form-label">Quick Start</label>
                <div className="template-preset-grid">
                  {TEMPLATE_PRESETS.map((preset) => (
                    <button key={preset.key} type="button" className="template-preset-btn"
                      onClick={() => handleApplyPreset(preset)}>
                      <span className="template-preset-label">{preset.label}</span>
                      <span className={`category-badge preset-category-${preset.category.toLowerCase()}`}>{preset.category}</span>
                    </button>
                  ))}
                </div>
                <span className="form-hint">Pick a starting point, then edit any field below before submitting.</span>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Template Name *</label>
                <input type="text" className={`form-input ${formErrors.name ? 'error' : ''}`}
                  value={templateForm.name}
                  disabled={!!editingTemplate}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="e.g., order_update" />
                {formErrors.name && <span className="form-error">{formErrors.name}</span>}
                <span className="form-hint">{editingTemplate ? 'Name is locked when editing' : 'Lowercase, underscores only'}</span>
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select className="form-select" value={templateForm.category}
                  onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}>
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utility</option>
                  <option value="AUTHENTICATION">Authentication</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Language</label>
              <select className="form-select" value={templateForm.language}
                disabled={!!editingTemplate}
                onChange={(e) => setTemplateForm({ ...templateForm, language: e.target.value })}>
                <option value="en">English</option>
                <option value="en_US">English (US)</option>
                <option value="hi">Hindi</option>
                <option value="mr">Marathi</option>
                <option value="ta">Tamil</option>
                <option value="te">Telugu</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Header</label>
              <select className="form-select" value={templateForm.headerType}
                onChange={(e) => setTemplateForm({ ...templateForm, headerType: e.target.value })}>
                <option value="none">None</option>
                <option value="text">Text</option>
                <option value="image">Image</option>
              </select>
            </div>

            {templateForm.headerType === 'text' && (
              <div className="form-group">
                <label className="form-label">Header Text</label>
                <input type="text" className="form-input" value={templateForm.headerText}
                  onChange={(e) => setTemplateForm({ ...templateForm, headerText: e.target.value })}
                  placeholder="Header text content" maxLength={60} />
              </div>
            )}

            {templateForm.headerType === 'image' && (
              <div className="form-group">
                <label className="form-label">Header Image</label>
                <div className="image-upload-area">
                  <label className={`upload-btn ${uploading ? 'disabled' : ''}`}>
                    <UploadSimple size={16} />
                    {uploading ? 'Uploading...' : 'Upload Image'}
                    <input type="file" accept="image/jpeg,image/png,image/webp"
                      onChange={handleHeaderImageUpload} disabled={uploading} hidden />
                  </label>
                  {templateForm.headerImageUrl && (
                    <div className="image-preview">
                      <img src={templateForm.headerImageUrl} alt="Header preview" />
                      <button className="image-preview-remove"
                        onClick={() => setTemplateForm({ ...templateForm, headerImageUrl: '' })} type="button">
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
                {formErrors.headerImage && <span className="form-error">{formErrors.headerImage}</span>}
                <span className="form-hint">JPEG, PNG, or WebP. Max 5MB.</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Body *</label>
              <textarea className={`form-input form-textarea ${formErrors.body ? 'error' : ''}`}
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                placeholder="Hello {{1}}, your order {{2}} is ready for pickup."
                rows={4} maxLength={1024} />
              {formErrors.body && <span className="form-error">{formErrors.body}</span>}
              <span className="form-hint">Use {'{{1}}'}, {'{{2}}'} etc. for variables. Max 1024 chars.</span>
            </div>

            <div className="form-group">
              <label className="form-label">Footer</label>
              <input type="text" className="form-input" value={templateForm.footer}
                onChange={(e) => setTemplateForm({ ...templateForm, footer: e.target.value })}
                placeholder="Optional footer text" maxLength={60} />
            </div>

            <div className="form-group">
              <div className="form-label-row">
                <label className="form-label">Quick Reply Buttons</label>
                {templateForm.buttons.length < 3 && (
                  <button className="add-button-btn" onClick={handleAddButton} type="button">
                    <Plus size={14} /> Add Button
                  </button>
                )}
              </div>
              {templateForm.buttons.map((btn, index) => (
                <div key={index} className="button-row">
                  <input type="text" className="form-input" value={btn.text}
                    onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                    placeholder={`Button ${index + 1} text`} maxLength={25} />
                  <button className="remove-button-btn" onClick={() => handleRemoveButton(index)} type="button">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <span className="form-hint">
                Up to 3 quick reply buttons
                {editingTemplate ? '. Flow / URL buttons on this template are kept automatically.' : ''}
              </span>
            </div>

            <div className="modal-actions">
              <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingTemplate(null); }}>Cancel</Button>
              <Button variant="primary" onClick={handleSaveTemplate} disabled={saving}>
                {saving
                  ? (editingTemplate ? 'Saving...' : 'Creating...')
                  : (editingTemplate ? 'Save Changes' : 'Create Template')}
              </Button>
            </div>
          </div>
        </Modal>

        {/* ═══════════════ VIEW TEMPLATE MODAL ═══════════════ */}
        <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} title="Template Details">
          {selectedTemplate && (
            <div className="template-detail">
              <div className="detail-row">
                <span className="detail-label">Name</span>
                <span className="detail-value">{selectedTemplate.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status</span>
                <span className="detail-value">{getStatusDisplay(selectedTemplate.status)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Category</span>
                <span className="detail-value"><span className="category-badge">{selectedTemplate.category}</span></span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Language</span>
                <span className="detail-value">{selectedTemplate.language}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">ID</span>
                <span className="detail-value mono">{selectedTemplate.id}</span>
              </div>

              {selectedTemplate.components && selectedTemplate.components.length > 0 && (
                <div className="detail-components">
                  <h4>Components</h4>
                  {selectedTemplate.components.map((comp, i) => (
                    <div key={i} className="component-block">
                      <span className="component-type">{comp.type}</span>
                      {comp.text && <p className="component-text">{comp.text}</p>}
                      {comp.format && <span className="component-format">Format: {comp.format}</span>}
                      {comp.buttons && (
                        <div className="component-buttons">
                          {comp.buttons.map((btn, j) => (
                            <span key={j} className="component-button">{btn.text}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-actions">
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
                <Button variant="primary" onClick={() => handleEditTemplate(selectedTemplate)}>
                  <NotePencil size={16} /> Edit Template
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* ═══════════════ DELETE TEMPLATE MODAL ═══════════════ */}
        <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Template">
          <div className="delete-confirmation">
            <Warning size={48} className="warning-icon" />
            <p>Are you sure you want to delete <strong>{deletingTemplate?.name}</strong>?</p>
            <p className="warning-text">This will permanently delete the template from Meta. This action cannot be undone.</p>
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
              <Button variant="danger" onClick={handleDeleteTemplate} disabled={saving}>
                {saving ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* ═══════════════ CHAT VIEW MODAL ═══════════════ */}
        <Modal isOpen={showChatModal} onClose={() => { setShowChatModal(false); setConversationDetail(null); }}
          title="Conversation" size="large">
          {conversationDetail ? (
            <div className="chat-modal-content">
              <div className="chat-header-info">
                <div className="chat-customer-info">
                  <UserCircle size={32} />
                  <div>
                    <strong>{conversationDetail.customerName || 'Unknown'}</strong>
                    <span className="chat-phone">{conversationDetail.phone}</span>
                  </div>
                </div>
                <div className="chat-meta">
                  <span className={`conv-status ${conversationDetail.isActive ? 'conv-active' : 'conv-ended'}`}>
                    {conversationDetail.isActive ? 'Active' : 'Ended'}
                  </span>
                  <span className="intent-badge">{conversationDetail.intent}</span>
                </div>
              </div>

              <div className="chat-messages">
                <ConversationMessages messages={conversationDetail.messages} formatDateTime={formatDateTime} />
              </div>

              {conversationDetail.isActive && (
                <div className="chat-reply-bar">
                  <input type="text" className="chat-reply-input" placeholder="Type a reply..."
                    value={adminReply} onChange={(e) => setAdminReply(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendReply()} />
                  <button className="chat-send-btn" onClick={handleSendReply} disabled={sendingReply || !adminReply.trim()}>
                    <PaperPlaneRight size={18} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="loading-state"><ArrowsClockwise className="loading-spinner" size={24} /><p>Loading...</p></div>
          )}
        </Modal>

        {/* ═══════════════ CREATE CAMPAIGN MODAL ═══════════════ */}
        <Modal isOpen={showCreateCampaignModal} onClose={() => setShowCreateCampaignModal(false)} title="Create Campaign">
          <div className="modal-form template-create-form">
            {campaignFormErrors.submit && (
              <div className="form-error-banner"><Warning size={18} />{campaignFormErrors.submit}</div>
            )}

            <div className="form-group">
              <label className="form-label">Campaign Name *</label>
              <input type="text" className={`form-input ${campaignFormErrors.name ? 'error' : ''}`}
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                placeholder="e.g., Weekend Offer March 2026" />
              {campaignFormErrors.name && <span className="form-error">{campaignFormErrors.name}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Template *</label>
                {templates.filter(t => t.status === 'APPROVED').length > 0 ? (
                  <select className={`form-select ${campaignFormErrors.templateName ? 'error' : ''}`}
                    value={campaignForm.templateName}
                    onChange={(e) => {
                      const selected = templates.find(t => t.name === e.target.value);
                      setCampaignForm({
                        ...campaignForm,
                        templateName: e.target.value,
                        templateLanguage: selected?.language || campaignForm.templateLanguage
                      });
                    }}>
                    <option value="">Select a template...</option>
                    {templates
                      .filter(t => t.status === 'APPROVED')
                      .map(t => (
                        <option key={t.id} value={t.name}>
                          {t.name} ({t.language}) — {t.category}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input type="text" className={`form-input ${campaignFormErrors.templateName ? 'error' : ''}`}
                    value={campaignForm.templateName}
                    onChange={(e) => setCampaignForm({ ...campaignForm, templateName: e.target.value })}
                    placeholder="e.g., the_weekend_lazy_angle" />
                )}
                {campaignFormErrors.templateName && <span className="form-error">{campaignFormErrors.templateName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Language</label>
                <select className="form-select" value={campaignForm.templateLanguage}
                  onChange={(e) => setCampaignForm({ ...campaignForm, templateLanguage: e.target.value })}>
                  <option value="en">English</option>
                  <option value="en_US">English (US)</option>
                  <option value="hi">Hindi</option>
                  <option value="te">Telugu</option>
                </select>
                <span className="form-hint">Auto-filled from selected template</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Target Audience</label>
              <select className="form-select" value={campaignForm.targetType}
                onChange={(e) => { setCampaignForm({ ...campaignForm, targetType: e.target.value }); setAudiencePreview(null); }}>
                <option value="all">All Customers</option>
                <option value="filtered">Filtered Customers</option>
              </select>
            </div>

            {campaignForm.targetType === 'filtered' && (
              <div className="campaign-filters-section">
                <div className="form-group">
                  <label className="form-label">Pincodes (comma-separated)</label>
                  <input type="text" className="form-input"
                    value={campaignForm.filters.pincodes.join(', ')}
                    onChange={(e) => setCampaignForm({
                      ...campaignForm,
                      filters: { ...campaignForm.filters, pincodes: e.target.value.split(',').map(p => p.trim()).filter(Boolean) }
                    })}
                    placeholder="e.g., 500081, 500082" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <input type="checkbox" checked={campaignForm.filters.hasOrders}
                        onChange={(e) => setCampaignForm({
                          ...campaignForm,
                          filters: { ...campaignForm.filters, hasOrders: e.target.checked, noOrders: e.target.checked ? false : campaignForm.filters.noOrders }
                        })} />
                      Only customers with orders
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <input type="checkbox" checked={campaignForm.filters.noOrders}
                        onChange={(e) => setCampaignForm({
                          ...campaignForm,
                          filters: { ...campaignForm.filters, noOrders: e.target.checked, hasOrders: e.target.checked ? false : campaignForm.filters.hasOrders }
                        })} />
                      Only customers without orders
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="campaign-audience-preview">
              <Button variant="outline" onClick={handlePreviewAudience} disabled={previewingAudience}>
                <UsersThree size={16} />
                {previewingAudience ? 'Loading...' : 'Preview Audience'}
              </Button>
              {audiencePreview && (
                <div className="audience-preview-result">
                  <strong>{audiencePreview.count}</strong> customers will receive this campaign
                  {audiencePreview.sample && audiencePreview.sample.length > 0 && (
                    <span className="audience-sample">
                      Sample: {audiencePreview.sample.map(s => s.customerName).join(', ')}
                      {audiencePreview.count > audiencePreview.sample.length && '...'}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Flow Token (optional)</label>
              <input type="text" className="form-input"
                value={campaignForm.flowToken}
                onChange={(e) => setCampaignForm({ ...campaignForm, flowToken: e.target.value })}
                placeholder="e.g., weekend_campaign_2026" />
              <span className="form-hint">If your template has a Flow button, enter the flow_token here</span>
            </div>

            <div className="form-group">
              <label className="form-label">Schedule (optional)</label>
              <input type="datetime-local" className="form-input"
                value={campaignForm.scheduledAt || ''}
                onChange={(e) => setCampaignForm({ ...campaignForm, scheduledAt: e.target.value })}
                min={new Date().toISOString().slice(0, 16)} />
              <span className="form-hint">Leave empty to save as draft and send manually. Set a date/time to auto-send at that time.</span>
            </div>

            <div className="form-group">
              <div className="form-label-row">
                <label className="form-label">Template Parameters</label>
                <button className="add-button-btn" onClick={() => setCampaignForm({
                  ...campaignForm,
                  templateParams: [...campaignForm.templateParams, { name: '', defaultValue: '', useCustomerName: false }]
                })} type="button">
                  <Plus size={14} /> Add Param
                </button>
              </div>
              {campaignForm.templateParams.map((param, i) => (
                <div key={i} className="button-row" style={{ marginTop: '6px' }}>
                  <input type="text" className="form-input" value={param.name}
                    onChange={(e) => {
                      const params = [...campaignForm.templateParams];
                      params[i] = { ...params[i], name: e.target.value };
                      setCampaignForm({ ...campaignForm, templateParams: params });
                    }}
                    placeholder="Param name (e.g., customer)" style={{ flex: '0 0 120px' }} />
                  <input type="text" className="form-input" value={param.defaultValue}
                    onChange={(e) => {
                      const params = [...campaignForm.templateParams];
                      params[i] = { ...params[i], defaultValue: e.target.value };
                      setCampaignForm({ ...campaignForm, templateParams: params });
                    }}
                    placeholder="Default value" />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={param.useCustomerName}
                      onChange={(e) => {
                        const params = [...campaignForm.templateParams];
                        params[i] = { ...params[i], useCustomerName: e.target.checked };
                        setCampaignForm({ ...campaignForm, templateParams: params });
                      }} />
                    Use Name
                  </label>
                  <button className="remove-button-btn" onClick={() => {
                    setCampaignForm({
                      ...campaignForm,
                      templateParams: campaignForm.templateParams.filter((_, j) => j !== i)
                    });
                  }} type="button"><X size={14} /></button>
                </div>
              ))}
              <span className="form-hint">Parameters matching your template body variables</span>
            </div>

            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowCreateCampaignModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleSaveCampaign} disabled={campaignSaving}>
                {campaignSaving ? 'Creating...' : campaignForm.scheduledAt ? 'Schedule Campaign' : 'Create Draft'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* ═══════════════ CAMPAIGN DETAIL MODAL ═══════════════ */}
        <Modal isOpen={showCampaignDetailModal} onClose={() => { setShowCampaignDetailModal(false); setSelectedCampaign(null); }}
          title="Campaign Details" size="large">
          {selectedCampaign ? (
            <div className="campaign-detail">
              <div className="campaign-detail-header">
                <div>
                  <h3 style={{ margin: 0 }}>{selectedCampaign.name}</h3>
                  <span className={`status-badge ${getCampaignStatusStyle(selectedCampaign.status).className}`} style={{ marginTop: '8px', display: 'inline-flex' }}>
                    {getCampaignStatusStyle(selectedCampaign.status).label}
                  </span>
                </div>
                <div className="campaign-detail-actions">
                  {(selectedCampaign.status === 'draft' || selectedCampaign.status === 'scheduled') && (
                    <Button variant="primary" onClick={() => { handleSendCampaign(selectedCampaign._id); setShowCampaignDetailModal(false); }}
                      disabled={sendingCampaign}>
                      <Play size={16} /> Send Now
                    </Button>
                  )}
                  {(selectedCampaign.status === 'sending' || selectedCampaign.status === 'scheduled') && (
                    <Button variant="danger" onClick={() => handleCancelCampaign(selectedCampaign._id)}>
                      <Stop size={16} /> Cancel
                    </Button>
                  )}
                </div>
              </div>

              <div className="campaign-stats-grid">
                <div className="campaign-stat-card">
                  <span className="campaign-stat-label">Total Recipients</span>
                  <span className="campaign-stat-value">{selectedCampaign.totalRecipients || 0}</span>
                </div>
                <div className="campaign-stat-card">
                  <span className="campaign-stat-label">Sent</span>
                  <span className="campaign-stat-value" style={{ color: 'var(--success)' }}>{selectedCampaign.sent || 0}</span>
                </div>
                <div className="campaign-stat-card">
                  <span className="campaign-stat-label">Failed</span>
                  <span className="campaign-stat-value" style={{ color: 'var(--danger)' }}>{selectedCampaign.failed || 0}</span>
                </div>
                <div className="campaign-stat-card">
                  <span className="campaign-stat-label">Success Rate</span>
                  <span className="campaign-stat-value">
                    {selectedCampaign.totalRecipients > 0
                      ? Math.round((selectedCampaign.sent / selectedCampaign.totalRecipients) * 100) + '%'
                      : '-'}
                  </span>
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">Template</span>
                <span className="detail-value mono">{selectedCampaign.templateName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Audience</span>
                <span className="detail-value">{selectedCampaign.targetType === 'all' ? 'All Customers' : 'Filtered'}</span>
              </div>
              {selectedCampaign.flowToken && (
                <div className="detail-row">
                  <span className="detail-label">Flow Token</span>
                  <span className="detail-value mono">{selectedCampaign.flowToken}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">{formatDateTime(selectedCampaign.createdAt)}</span>
              </div>
              {selectedCampaign.scheduledAt && (
                <div className="detail-row">
                  <span className="detail-label">Scheduled</span>
                  <span className="detail-value">{formatDateTime(selectedCampaign.scheduledAt)}</span>
                </div>
              )}
              {selectedCampaign.startedAt && (
                <div className="detail-row">
                  <span className="detail-label">Started</span>
                  <span className="detail-value">{formatDateTime(selectedCampaign.startedAt)}</span>
                </div>
              )}
              {selectedCampaign.completedAt && (
                <div className="detail-row">
                  <span className="detail-label">Completed</span>
                  <span className="detail-value">{formatDateTime(selectedCampaign.completedAt)}</span>
                </div>
              )}

              {/* Failures list */}
              {selectedCampaign.failures && selectedCampaign.failures.length > 0 && (
                <div className="campaign-failures-section">
                  <h4 style={{ margin: '16px 0 8px', fontSize: '0.875rem', fontWeight: 700, borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    Failed Deliveries ({selectedCampaign.failures.length})
                  </h4>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Phone</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCampaign.failures.map((f, i) => (
                          <tr key={i}>
                            <td>{f.customerName}</td>
                            <td className="mono">{f.phone}</td>
                            <td style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>{f.error?.substring(0, 80)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <Button variant="outline" onClick={() => { setShowCampaignDetailModal(false); setSelectedCampaign(null); }}>Close</Button>
              </div>
            </div>
          ) : (
            <div className="loading-state"><ArrowsClockwise className="loading-spinner" size={24} /><p>Loading...</p></div>
          )}
        </Modal>

        {/* ═══════════════ DELETE CAMPAIGN MODAL ═══════════════ */}
        <Modal isOpen={showDeleteCampaignModal} onClose={() => setShowDeleteCampaignModal(false)} title="Delete Campaign">
          <div className="delete-confirmation">
            <Warning size={48} className="warning-icon" />
            <p>Are you sure you want to delete campaign <strong>{deletingCampaignItem?.name}</strong>?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowDeleteCampaignModal(false)}>Cancel</Button>
              <Button variant="danger" onClick={handleDeleteCampaignConfirm} disabled={campaignSaving}>
                {campaignSaving ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* ═══════════════ COMPLAINT DETAIL MODAL ═══════════════ */}
        <Modal isOpen={showComplaintModal}
          onClose={() => { setShowComplaintModal(false); setConversationDetail(null); setSelectedComplaint(null); }}
          title="Complaint Details" size="large">
          {conversationDetail && selectedComplaint ? (
            <div className="complaint-modal-content">
              {/* Complaint Info Bar */}
              <div className="complaint-info-bar">
                <div className="complaint-info-left">
                  <div className="chat-customer-info">
                    <UserCircle size={32} />
                    <div>
                      <strong>{conversationDetail.customerName || 'Unknown'}</strong>
                      <span className="chat-phone">{conversationDetail.phone}</span>
                    </div>
                  </div>
                </div>
                <div className="complaint-info-right">
                  <div className="complaint-control">
                    <label>Status:</label>
                    <select className="filter-select"
                      value={conversationDetail.complaintStatus || 'open'}
                      onChange={(e) => handleUpdateComplaintStatus(selectedComplaint.id, e.target.value)}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div className="complaint-control">
                    <label>Priority:</label>
                    <select className="filter-select"
                      value={conversationDetail.complaintPriority || 'medium'}
                      onChange={(e) => handleUpdateComplaintPriority(selectedComplaint.id, e.target.value)}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Complaint Context */}
              {(selectedComplaint.complaintType || selectedComplaint.complaintOrderId) && (
                <div className="complaint-context">
                  {selectedComplaint.complaintType && (
                    <div className="context-item">
                      <WarningCircle size={16} />
                      <span>Type: <strong>{selectedComplaint.complaintType}</strong></span>
                    </div>
                  )}
                  {selectedComplaint.complaintOrderId && (
                    <div className="context-item">
                      <FileText size={16} />
                      <span>Order: <strong className="mono">{selectedComplaint.complaintOrderId}</strong></span>
                    </div>
                  )}
                </div>
              )}

              <div className="complaint-split-view">
                {/* Chat History */}
                <div className="complaint-chat-section">
                  <h4>Conversation History</h4>
                  <div className="chat-messages complaint-chat-messages">
                    <ConversationMessages messages={conversationDetail.messages} formatDateTime={formatDateTime} />
                  </div>

                  {/* Reply bar */}
                  <div className="chat-reply-bar">
                    <input type="text" className="chat-reply-input" placeholder="Send reply via WhatsApp..."
                      value={adminReply} onChange={(e) => setAdminReply(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendReply()} />
                    <button className="chat-send-btn" onClick={handleSendReply} disabled={sendingReply || !adminReply.trim()}>
                      <PaperPlaneRight size={18} />
                    </button>
                  </div>
                </div>

                {/* Admin Notes */}
                <div className="complaint-notes-section">
                  <h4>Admin Notes</h4>
                  <div className="admin-notes-list">
                    {(conversationDetail.adminNotes || []).length === 0 && (
                      <p className="text-muted no-notes">No notes yet</p>
                    )}
                    {(conversationDetail.adminNotes || []).map((note, i) => (
                      <div key={i} className="admin-note-item">
                        <div className="admin-note-header">
                          <span className="admin-note-author">{note.addedBy}</span>
                          <span className="admin-note-time">{formatDateTime(note.addedAt)}</span>
                        </div>
                        <p className="admin-note-text">{note.note}</p>
                      </div>
                    ))}
                  </div>
                  <div className="add-note-bar">
                    <textarea className="add-note-input" placeholder="Add a note..."
                      value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={2} />
                    <Button variant="primary" onClick={handleAddNote} disabled={addingNote || !adminNote.trim()}>
                      <NotePencil size={16} />
                      {addingNote ? 'Adding...' : 'Add Note'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="loading-state"><ArrowsClockwise className="loading-spinner" size={24} /><p>Loading...</p></div>
          )}
        </Modal>
      </div>
    </>
  );
}

export default WhatsApp;
